import {
  OverflowMenu,
  OverflowMenuItem,
  Table,
  TableBatchAction,
  TableBatchActions,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent,
} from '@carbon/react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { IconComponent } from '../../types';
import { cx } from '../../utils';
import styles from './CdDataTable.module.scss';

export interface CdDataTableColumn<Row> {
  key: string;
  header: string;
  /** Custom cell renderer; defaults to `String(row[key])`. */
  render?: (row: Row) => ReactNode;
}

export interface CdDataTableBatchAction {
  id: string;
  label: string;
  icon?: IconComponent;
  disabled?: boolean;
  onClick: (selectedIds: string[]) => void;
}

export interface CdDataTableRowAction<Row> {
  id: string;
  label: string;
  onSelect: (row: Row) => void;
  danger?: boolean;
}

export interface CdDataTableProps<Row extends { id: string }> {
  /** Accessible table name. */
  label: string;
  rows: Row[];
  columns: CdDataTableColumn<Row>[];
  /** Controlled selection. Omit `onSelectionChange` to render a non-selectable table. */
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  batchActions?: CdDataTableBatchAction[];
  rowActions?: CdDataTableRowAction<Row>[];
  /**
   * Leading toolbar (record count, Refresh, Manage columns). Hidden by the navy
   * batch-actions bar while rows are selected — do not render the same actions above the table.
   */
  toolbar?: ReactNode;
  /** Rendered instead of the body when there are no rows. */
  emptyState?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Footer (e.g. PaginationBar). */
  footer?: ReactNode;
  /**
   * Pin the toolbar and column headers while the window scrolls past the table. They stick at
   * `--cd-table-sticky-top` (defaults to below the app header).
   */
  stickyHeader?: boolean;
  className?: string;
}

const cellValue = (row: object, key: string): ReactNode => {
  const value = (row as Record<string, unknown>)[key];
  if (typeof value === 'string' || typeof value === 'number') return value;
  return value == null ? '' : JSON.stringify(value);
};

/**
 * Controlled, selectable data table built from Carbon table primitives.
 * The navy batch-actions bar replaces the toolbar while rows are selected (Figma index page).
 */
export const CdDataTable = <Row extends { id: string }>({
  label,
  rows,
  columns,
  selectedIds = [],
  onSelectionChange,
  batchActions = [],
  rowActions = [],
  toolbar,
  emptyState,
  size = 'md',
  footer,
  stickyHeader = false,
  className,
}: CdDataTableProps<Row>) => {
  const tableId = useId();
  const stickyRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const cloneRef = useRef<HTMLDivElement>(null);
  const [headPinned, setHeadPinned] = useState(false);
  const selectable = Boolean(onSelectionChange);
  const selected = new Set(selectedIds);
  const pageIds = rows.map((r) => r.id);
  const selectedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const allSelected = rows.length > 0 && selectedOnPage === rows.length;

  const toggleRow = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange?.([...next]);
  };

  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    onSelectionChange?.([...next]);
  };

  const showBatch = selectable && selectedIds.length > 0 && batchActions.length > 0;
  const showToolbar = Boolean(toolbar) || showBatch;

  // The scroll wrapper (horizontal overflow) stops `position: sticky` on <thead> from tracking the
  // window, so a header-only copy pins below the toolbar instead. It mirrors the real column
  // widths and horizontal scroll, and shows only once the real header has scrolled under it.
  useEffect(() => {
    const scroller = scrollRef.current;
    const table = scroller?.querySelector('table');
    if (!stickyHeader || !scroller || !table) return;

    let frame = 0;
    const sync = () => {
      frame = 0;
      const sticky = stickyRef.current;
      const clone = cloneRef.current;
      const head = table.tHead;
      if (!sticky || !clone || !head) return;

      const tableRect = table.getBoundingClientRect();
      const cloneTable = clone.querySelector('table');
      const cloneCells = clone.querySelectorAll('th');
      head.querySelectorAll('th').forEach((cell, i) => {
        const target = cloneCells[i];
        if (target) target.style.width = `${cell.getBoundingClientRect().width}px`;
      });
      if (cloneTable) {
        // Follows horizontal scroll by position, whichever wrapper is doing the scrolling.
        cloneTable.style.width = `${tableRect.width}px`;
        cloneTable.style.transform = `translateX(${tableRect.left - clone.getBoundingClientRect().left}px)`;
      }

      const pinTop = sticky.getBoundingClientRect().bottom;
      const headRect = head.getBoundingClientRect();
      setHeadPinned(headRect.top < pinTop - 0.5 && tableRect.bottom > pinTop + headRect.height);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };

    sync();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Scroll events do not bubble; capture catches Carbon's inner overflow wrapper too.
    scroller.addEventListener('scroll', schedule, { capture: true, passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(table);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      scroller.removeEventListener('scroll', schedule, { capture: true });
      observer.disconnect();
    };
  }, [stickyHeader, rows, columns, selectable]);

  // Keep the copy out of the tab order; keyboard and screen reader users get the real header.
  useLayoutEffect(() => {
    cloneRef.current
      ?.querySelectorAll<HTMLElement>('input, button, a')
      .forEach((el) => (el.tabIndex = -1));
  });

  const headerRow = (copy: boolean) => (
    <TableRow>
      {selectable && (
        <TableSelectAll
          id={`${tableId}-all${copy ? '-pinned' : ''}`}
          name={`${tableId}-all${copy ? '-pinned' : ''}`}
          ariaLabel="Select all rows on this page"
          checked={allSelected}
          indeterminate={selectedOnPage > 0 && !allSelected}
          onSelect={toggleAll}
        />
      )}
      {columns.map((col) => (
        <TableHeader key={col.key} scope="col">
          {col.header}
        </TableHeader>
      ))}
      {rowActions.length > 0 && (
        <TableHeader scope="col">
          <span className={styles.srOnly}>Row actions</span>
        </TableHeader>
      )}
    </TableRow>
  );

  const toolbarBar = showToolbar && (
    <TableToolbar className={styles.toolbar} size="sm" aria-label={`${label} actions`}>
      {batchActions.length > 0 && (
        <TableBatchActions
          className={styles.batch}
          shouldShowBatchActions={showBatch}
          totalSelected={selectedIds.length}
          onCancel={() => onSelectionChange?.([])}
        >
          {batchActions.map((action) => (
            <TableBatchAction
              key={action.id}
              renderIcon={action.icon}
              onClick={() => action.onClick(selectedIds)}
            >
              {action.label}
            </TableBatchAction>
          ))}
        </TableBatchActions>
      )}
      {toolbar && (
        <TableToolbarContent className={styles.toolbarContent}>{toolbar}</TableToolbarContent>
      )}
    </TableToolbar>
  );

  return (
    <TableContainer className={cx(styles.container, className)}>
      {stickyHeader ? (
        <div ref={stickyRef} className={styles.stickyHead}>
          {toolbarBar}
          {rows.length > 0 && (
            <div
              ref={cloneRef}
              className={cx(styles.headClone, headPinned && styles.headCloneVisible)}
              aria-hidden
            >
              <Table size={size} className={styles.table}>
                <TableHead>{headerRow(true)}</TableHead>
              </Table>
            </div>
          )}
        </div>
      ) : (
        toolbarBar
      )}
      <div ref={scrollRef} className={styles.scroll}>
        <Table aria-label={label} size={size} className={styles.table}>
          <TableHead>{headerRow(false)}</TableHead>
          {rows.length > 0 && (
            <TableBody>
              {rows.map((row) => {
                const isSelected = selected.has(row.id);
                return (
                  <TableRow
                    key={row.id}
                    className={cx(isSelected && styles.selectedRow)}
                    aria-selected={selectable ? isSelected : undefined}
                  >
                    {selectable && (
                      <TableSelectRow
                        id={`${tableId}-${row.id}`}
                        name={`${tableId}-row`}
                        ariaLabel={`Select row ${row.id}`}
                        checked={isSelected}
                        onSelect={() => toggleRow(row.id)}
                      />
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.key}>
                        {col.render ? col.render(row) : cellValue(row, col.key)}
                      </TableCell>
                    ))}
                    {rowActions.length > 0 && (
                      <TableCell className={styles.actionsCell}>
                        <OverflowMenu
                          aria-label="Row actions"
                          iconDescription="Row actions"
                          size="sm"
                          flipped
                        >
                          {rowActions.map((action) => (
                            <OverflowMenuItem
                              key={action.id}
                              itemText={action.label}
                              isDelete={action.danger}
                              onClick={() => action.onSelect(row)}
                            />
                          ))}
                        </OverflowMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
        {rows.length === 0 && emptyState}
      </div>
      {footer}
    </TableContainer>
  );
};
